'use client';

import { useEffect } from 'react';

import { animate } from 'motion/react';

import { REVEAL_EASE } from '@/components/reveal';

type Playback = { stop: () => void };

let playback: Playback | null = null;
let detach: (() => void) | null = null;

/** Hash id if this href points at an element on the current page. */
function samePageHashId(href: string): string | null {
  if (href.startsWith('#') && href.length > 1) return href.slice(1);
  try {
    const url = new URL(href, window.location.href);
    if (url.origin !== window.location.origin) return null;
    if (url.pathname !== window.location.pathname) return null;
    if (url.hash.length <= 1) return null;
    return url.hash.slice(1);
  } catch {
    return null;
  }
}

/**
 * The site's one scroll animation. Exported so anything that moves the page — the header logo's
 * jump to the top, in-page section links — travels on the same curve; `behavior: 'smooth'` is a
 * fixed browser curve that reads as a different control next to this one.
 */
export function smoothScrollToY(to: number) {
  detach?.();

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (reducedMotion) {
    window.scrollTo({ top: to });
    return;
  }

  const from = window.scrollY;
  const distance = Math.abs(to - from);
  if (distance < 2) return;

  function release() {
    window.removeEventListener('wheel', interrupt);
    window.removeEventListener('touchstart', interrupt);
    playback = null;
    detach = null;
  }

  function interrupt() {
    playback?.stop();
    release();
  }

  detach = interrupt;

  // Ease-out expo, duration scaled to distance so a short hop is snappy and
  // the long drop from the hero to How it works still lands, not snaps.
  playback = animate(from, to, {
    duration: Math.min(1.15, Math.max(0.55, distance / 1500)),
    ease: REVEAL_EASE,
    onUpdate(value) {
      window.scrollTo(0, value);
    },
    onComplete: release,
  });

  window.addEventListener('wheel', interrupt, { passive: true });
  window.addEventListener('touchstart', interrupt, { passive: true });
}

/** Scroll so `target` sits just below the fixed chrome, honouring its own scroll-margin. */
function scrollToTarget(target: HTMLElement) {
  const margin = Number.parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
  smoothScrollToY(target.getBoundingClientRect().top + window.scrollY - margin);
}

export function SmoothHashScroll() {
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      ) {
        return;
      }
      const eventTarget = e.target;
      if (!(eventTarget instanceof Element)) return;
      const anchor = eventTarget.closest('a');
      if (!anchor || anchor.hasAttribute('download')) return;
      if (anchor.target && anchor.target !== '_self') return;
      const href = anchor.getAttribute('href');
      if (!href) return;
      const id = samePageHashId(href);
      if (!id) return;
      const section = document.getElementById(id);
      if (!section) return;
      e.preventDefault();
      // Land on the section's kicker if it has one — the section box starts a whole band of top
      // padding above its heading, so scrolling to the box overshoots the content by 64–112px.
      const target = section.querySelector<HTMLElement>('[data-section-anchor]') ?? section;
      scrollToTarget(target);
      history.pushState(null, '', `#${id}`);
      // Move keyboard focus into the target so Tab continues from there.
      // tabindex=-1 lets non-focusable elements (like <section>) receive focus
      // programmatically without entering the tab order.
      // Focus the section, not the kicker: the kicker is a scroll anchor, the section is the
      // landmark a reader should continue from.
      if (!section.hasAttribute('tabindex')) section.setAttribute('tabindex', '-1');
      section.focus({ preventScroll: true });
    }
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);
  return null;
}
